/**
 * PATH: packages/job-hub/src/jobs/qa/InlineRenameInput.tsx
 * FILE_ID: JOBHUB-QA-RENAME-INPUT-001
 * PURPOSE: Tiny controlled inline-edit field used to rename a QA list or area
 *          in place. Auto-focuses + selects, commits on Enter / save, cancels
 *          on Escape, and no-ops on an empty or unchanged value.
 * WHY:     Operator (2026-06-18): "I can't change names". The QA list + area
 *          names were display-only; this gives a consistent rename affordance
 *          without nesting an <input> inside the selecting <button>.
 * DEPENDENCIES:
 * - ./inlineRename: decideRename (pure trim/changed/valid rule).
 */
import { useEffect, useRef, useState } from 'react'
import { Check, Loader2, X } from 'lucide-react'
import { cn } from '@quotech/tenant-shared/lib/utils'
import { decideRename } from './inlineRename'

export function InlineRenameInput({
  initialValue,
  onSubmit,
  onCancel,
  busy = false,
  className,
  ariaLabel = 'Rename',
}: {
  initialValue: string
  onSubmit: (value: string) => void
  onCancel: () => void
  busy?: boolean
  className?: string
  ariaLabel?: string
}) {
  const [draft, setDraft] = useState(initialValue)
  const ref = useRef<HTMLInputElement | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const commitRef = useRef<() => void>(() => {})

  const commit = () => {
    const { changed, value } = decideRename(draft, initialValue)
    if (changed) onSubmit(value)
    else onCancel()
  }
  commitRef.current = commit

  useEffect(() => {
    ref.current?.focus()
    ref.current?.select()
  }, [])

  useEffect(() => {
    let detach: (() => void) | undefined
    const frame = requestAnimationFrame(() => {
      const onPointerDown = (event: PointerEvent) => {
        const node = wrapRef.current
        if (!node || node.contains(event.target as Node)) return
        commitRef.current()
      }
      document.addEventListener('pointerdown', onPointerDown)
      detach = () => document.removeEventListener('pointerdown', onPointerDown)
    })
    return () => {
      cancelAnimationFrame(frame)
      detach?.()
    }
  }, [])

  return (
    <div ref={wrapRef} className={cn('flex items-center gap-1', className)}>
      <input
        ref={ref}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            onCancel()
          }
        }}
        disabled={busy}
        aria-label={ariaLabel}
        className="min-w-0 flex-1 rounded-md border border-primary bg-background px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
      />
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          commit()
        }}
        disabled={busy}
        title="Save"
        aria-label="Save name"
        className="shrink-0 rounded-md p-1 text-emerald-600 hover:bg-accent disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Check className="h-3.5 w-3.5" aria-hidden />}
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onCancel()
        }}
        title="Cancel"
        aria-label="Cancel rename"
        className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-accent"
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  )
}

export default InlineRenameInput
